import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ArrowLeft, Save, RefreshCw, Play, Trash2, Plus,
  Film, Clock, Users, Settings, AlertCircle, 
  Check, X, Loader2, ChevronDown, ChevronUp
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { useAuthStore } from "../lib/store";

const BASE_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function StoryEditor({ story, onClose, onRefresh }) {
  const [storyConfig, setStoryConfig] = useState(story?.story_config || {});
  const [expandedScene, setExpandedScene] = useState(null);
  const [saving, setSaving] = useState(false);
  const [regeneratingScene, setRegeneratingScene] = useState(null);
  const token = useAuthStore((state) => state.token);

  useEffect(() => {
    if (story?.story_config) {
      setStoryConfig(story.story_config);
    }
  }, [story]);

  const handleUpdateScene = async (sceneIndex, updates) => {
    try {
      setSaving(true);
      
      const response = await fetch(`${BASE_URL}/api/stories/${story.id}/scene`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          scene_index: sceneIndex,
          scene_data: updates,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setStoryConfig(data.story_config);
        toast.success(`Scene ${sceneIndex + 1} updated`);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update scene");
      }
    } catch (error) {
      console.error("Error updating scene:", error);
      toast.error("Failed to update scene");
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerateScene = async (sceneIndex) => {
    try {
      setRegeneratingScene(sceneIndex);
      
      const response = await fetch(`${BASE_URL}/api/stories/${story.id}/regenerate-scene`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ scene_index: sceneIndex }),
      });

      if (response.ok) {
        toast.success(`Scene ${sceneIndex + 1} regeneration started!`);
        if (onRefresh) onRefresh();
      } else {
        const error = await response.json();
        toast.error(error.detail?.message || error.detail || "Failed to regenerate scene");
      }
    } catch (error) {
      console.error("Error regenerating scene:", error);
      toast.error("Failed to regenerate scene");
    } finally {
      setRegeneratingScene(null);
    }
  };

  const handleUpdateStoryConfig = async (updates) => {
    try {
      setSaving(true);
      
      const response = await fetch(`${BASE_URL}/api/stories/${story.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updates),
      });

      if (response.ok) {
        const data = await response.json();
        setStoryConfig(data.story_config);
        toast.success("Story settings updated");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update story");
      }
    } catch (error) {
      console.error("Error updating story:", error);
      toast.error("Failed to update story");
    } finally {
      setSaving(false);
    }
  };

  const scenes = storyConfig.scenes || [];
  const characters = storyConfig.characters || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={onClose}
          className="flex items-center gap-2 text-muted-foreground hover:text-white transition-colors"
          data-testid="back-to-stories"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Stories
        </button>
        
        <Badge 
          className={`${
            story.status === "pending" || story.status === "completed"
              ? "bg-[#10B981]/20 text-[#10B981]"
              : story.status === "generating" || story.status === "regenerating"
              ? "bg-[#F59E0B]/20 text-[#F59E0B]"
              : "bg-red-500/20 text-red-400"
          }`}
        >
          {story.status}
        </Badge>
      </div>

      {/* Story Title */}
      <div className="space-y-2">
        <Label className="text-white">Story Title</Label>
        <Input
          value={storyConfig.title || ""}
          onChange={(e) => setStoryConfig({ ...storyConfig, title: e.target.value })}
          onBlur={() => handleUpdateStoryConfig({ title: storyConfig.title })}
          placeholder="Enter story title"
          className="bg-secondary/30 border-border/50"
          data-testid="story-title-input"
        />
      </div>

      {/* Characters Summary */}
      {characters.length > 0 && (
        <div className="p-3 rounded-lg bg-secondary/20 border border-border/30">
          <p className="text-sm text-muted-foreground mb-2">
            <Users className="w-4 h-4 inline mr-1" />
            Characters ({characters.length})
          </p>
          <div className="flex flex-wrap gap-2">
            {characters.map((char, idx) => (
              <Badge key={idx} variant="outline" className="text-xs">
                {char.name}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Scenes List */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Film className="w-5 h-5 text-[#A855F7]" />
            Scenes ({scenes.length})
          </h3>
        </div>

        {scenes.map((scene, idx) => (
          <motion.div
            key={idx}
            layout
            className="rounded-lg bg-secondary/30 border border-border/50 overflow-hidden"
          >
            {/* Scene Header */}
            <button
              onClick={() => setExpandedScene(expandedScene === idx ? null : idx)}
              className="w-full p-4 flex items-center justify-between hover:bg-secondary/40 transition-colors"
              data-testid={`scene-header-${idx}`}
            >
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-[#A855F7]/20 text-[#A855F7] flex items-center justify-center font-bold text-sm">
                  {idx + 1}
                </span>
                <div className="text-left">
                  <p className="font-medium text-white">
                    {scene.title || `Scene ${idx + 1}`}
                  </p>
                  <p className="text-xs text-muted-foreground flex items-center gap-2">
                    <Clock className="w-3 h-3" />
                    {scene.duration}s
                    {scene.characterIds?.length > 0 && (
                      <>
                        <span className="text-border">•</span>
                        <Users className="w-3 h-3" />
                        {scene.characterIds.length} character(s)
                      </>
                    )}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                {regeneratingScene === idx && (
                  <Badge className="bg-[#F59E0B]/20 text-[#F59E0B] text-xs">
                    <Loader2 className="w-3 h-3 animate-spin mr-1" />
                    Regenerating...
                  </Badge>
                )}
                {expandedScene === idx ? (
                  <ChevronUp className="w-5 h-5 text-muted-foreground" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-muted-foreground" />
                )}
              </div>
            </button>

            {/* Expanded Scene Editor */}
            <AnimatePresence>
              {expandedScene === idx && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="border-t border-border/30"
                >
                  <div className="p-4 space-y-4">
                    {/* Scene Title */}
                    <div className="space-y-2">
                      <Label className="text-sm text-muted-foreground">Scene Title</Label>
                      <Input
                        value={scene.title || ""}
                        onChange={(e) => {
                          const newScenes = [...scenes];
                          newScenes[idx] = { ...newScenes[idx], title: e.target.value };
                          setStoryConfig({ ...storyConfig, scenes: newScenes });
                        }}
                        onBlur={() => handleUpdateScene(idx, { title: scene.title })}
                        placeholder="Scene title"
                        className="bg-secondary/20 border-border/30"
                        data-testid={`scene-title-${idx}`}
                      />
                    </div>

                    {/* Narrative */}
                    <div className="space-y-2">
                      <Label className="text-sm text-muted-foreground">
                        Narrative / Dialogue
                        <span className="text-xs ml-2 text-[#10B981]">
                          (What should happen / be spoken)
                        </span>
                      </Label>
                      <Textarea
                        value={scene.narrative || ""}
                        onChange={(e) => {
                          const newScenes = [...scenes];
                          newScenes[idx] = { ...newScenes[idx], narrative: e.target.value };
                          setStoryConfig({ ...storyConfig, scenes: newScenes });
                        }}
                        onBlur={() => handleUpdateScene(idx, { narrative: scene.narrative })}
                        placeholder="Describe what happens in this scene or what the character says..."
                        className="bg-secondary/20 border-border/30 min-h-[100px]"
                        data-testid={`scene-narrative-${idx}`}
                      />
                    </div>

                    {/* Visual Prompt */}
                    <div className="space-y-2">
                      <Label className="text-sm text-muted-foreground">
                        Visual Prompt
                        <span className="text-xs ml-2 text-[#A855F7]">
                          (How the scene should look)
                        </span>
                      </Label>
                      <Textarea
                        value={scene.visualPrompt || ""}
                        onChange={(e) => {
                          const newScenes = [...scenes];
                          newScenes[idx] = { ...newScenes[idx], visualPrompt: e.target.value };
                          setStoryConfig({ ...storyConfig, scenes: newScenes });
                        }}
                        onBlur={() => handleUpdateScene(idx, { visualPrompt: scene.visualPrompt })}
                        placeholder="Describe the visual style, camera angles, lighting..."
                        className="bg-secondary/20 border-border/30 min-h-[80px]"
                        data-testid={`scene-visual-${idx}`}
                      />
                    </div>

                    {/* Duration */}
                    <div className="space-y-2">
                      <Label className="text-sm text-muted-foreground">Duration (seconds)</Label>
                      <Input
                        type="number"
                        min={5}
                        max={15}
                        value={scene.duration || 5}
                        onChange={(e) => {
                          const newScenes = [...scenes];
                          newScenes[idx] = { ...newScenes[idx], duration: parseInt(e.target.value) || 5 };
                          setStoryConfig({ ...storyConfig, scenes: newScenes });
                        }}
                        onBlur={() => handleUpdateScene(idx, { duration: scene.duration })}
                        className="bg-secondary/20 border-border/30 w-24"
                        data-testid={`scene-duration-${idx}`}
                      />
                      <p className="text-xs text-muted-foreground">
                        H3 Turbo supports 5-15 second clips
                      </p>
                    </div>

                    {/* Environment */}
                    <div className="space-y-2">
                      <Label className="text-sm text-muted-foreground">Environment (optional)</Label>
                      <Input
                        value={scene.environment || ""}
                        onChange={(e) => {
                          const newScenes = [...scenes];
                          newScenes[idx] = { ...newScenes[idx], environment: e.target.value };
                          setStoryConfig({ ...storyConfig, scenes: newScenes });
                        }}
                        onBlur={() => handleUpdateScene(idx, { environment: scene.environment })}
                        placeholder="Override global environment for this scene"
                        className="bg-secondary/20 border-border/30"
                        data-testid={`scene-environment-${idx}`}
                      />
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-2 pt-2">
                      <Button
                        size="sm"
                        className="bg-[#A855F7] hover:bg-[#A855F7]/80"
                        onClick={() => handleRegenerateScene(idx)}
                        disabled={regeneratingScene !== null || saving}
                        data-testid={`regenerate-scene-${idx}`}
                      >
                        {regeneratingScene === idx ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4 mr-1" />
                        )}
                        Regenerate This Scene
                      </Button>
                      
                      {saving && (
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Loader2 className="w-3 h-3 animate-spin" />
                          Saving...
                        </span>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ))}
      </div>

      {/* Global Settings */}
      <div className="p-4 rounded-lg bg-secondary/20 border border-border/30 space-y-4">
        <h4 className="font-medium text-white flex items-center gap-2">
          <Settings className="w-4 h-4 text-[#A855F7]" />
          Global Settings
        </h4>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">Style</Label>
            <select
              value={storyConfig.globalStyle || "cinematic"}
              onChange={(e) => {
                setStoryConfig({ ...storyConfig, globalStyle: e.target.value });
                handleUpdateStoryConfig({ globalStyle: e.target.value });
              }}
              className="w-full p-2 rounded-lg bg-secondary/30 border border-border/50 text-white text-sm"
              data-testid="story-style-select"
            >
              <option value="cinematic">Cinematic</option>
              <option value="anime">Anime</option>
              <option value="realistic">Realistic</option>
            </select>
          </div>
          
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">Aspect Ratio</Label>
            <select
              value={storyConfig.aspectRatio || "9:16"}
              onChange={(e) => {
                setStoryConfig({ ...storyConfig, aspectRatio: e.target.value });
                handleUpdateStoryConfig({ aspectRatio: e.target.value });
              }}
              className="w-full p-2 rounded-lg bg-secondary/30 border border-border/50 text-white text-sm"
              data-testid="story-aspect-select"
            >
              <option value="9:16">9:16 (TikTok/Reels)</option>
              <option value="16:9">16:9 (YouTube)</option>
              <option value="1:1">1:1 (Square)</option>
              <option value="4:5">4:5 (Instagram Feed)</option>
            </select>
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-sm text-muted-foreground">Global Environment</Label>
          <Input
            value={storyConfig.globalEnvironment || ""}
            onChange={(e) => setStoryConfig({ ...storyConfig, globalEnvironment: e.target.value })}
            onBlur={() => handleUpdateStoryConfig({ globalEnvironment: storyConfig.globalEnvironment })}
            placeholder="Default environment for all scenes"
            className="bg-secondary/30 border-border/50"
            data-testid="story-environment-input"
          />
        </div>
      </div>
    </div>
  );
}
